import React, { useState, useEffect } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { dailyChallengeAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Calendar, Trophy, Flame, Users, Clock, 
  Play, CheckCircle, Star, Medal, Zap, Snowflake,
  Crown, Award, TrendingUp
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

// Tier styles for leaderboard badges
const TIER_STYLES = {
  gold: 'bg-gradient-to-br from-yellow-400 to-amber-600',
  silver: 'bg-gradient-to-br from-gray-300 to-gray-500',
  bronze: 'bg-gradient-to-br from-orange-400 to-orange-700'
};

// Medal colors for podium
const MEDAL_STYLES = {
  1: { bg: 'from-yellow-400 to-amber-500', border: 'border-yellow-400', text: 'text-yellow-400', icon: Crown },
  2: { bg: 'from-gray-300 to-gray-400', border: 'border-gray-300', text: 'text-gray-300', icon: Medal },
  3: { bg: 'from-orange-400 to-orange-600', border: 'border-orange-400', text: 'text-orange-400', icon: Award }
};

export default function DailyChallenge() {
  useSEO('Daily Challenge & Leaderboard | JepPrep', 'Take on the daily trivia challenge and compete for top spots on the leaderboard.');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [todayLeaderboard, setTodayLeaderboard] = useState([]);
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState([]);
  const [alltimeLeaderboard, setAlltimeLeaderboard] = useState([]);
  const [rivals, setRivals] = useState(null);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('today');

  useEffect(() => {
    loadData();
    
    // Set up polling for live updates (every 10 seconds)
    const pollInterval = setInterval(() => {
      refreshData();
    }, 10000);
    
    return () => clearInterval(pollInterval);
  }, []);

  // Fetch rivals when period changes
  useEffect(() => {
    const fetchRivals = async () => {
      try {
        const rivalsRes = await dailyChallengeAPI.getRivals(leaderboardPeriod);
        setRivals(rivalsRes.data);
      } catch (error) {
        console.error('Failed to fetch rivals:', error);
      }
    };
    fetchRivals();
  }, [leaderboardPeriod]);

  const loadData = async () => {
    try {
      const [challengeRes, todayRes, weeklyRes, alltimeRes, rivalsRes] = await Promise.all([
        dailyChallengeAPI.get(),
        dailyChallengeAPI.getLeaderboard(),
        dailyChallengeAPI.getWeeklyLeaderboard().catch(() => ({ data: { leaderboard: [] } })),
        dailyChallengeAPI.getAllTimeLeaderboard().catch(() => ({ data: { leaderboard: [] } })),
        dailyChallengeAPI.getRivals('today').catch(() => ({ data: { rivals: [] } }))
      ]);
      setChallenge(challengeRes.data);
      setTodayLeaderboard(todayRes.data);
      setWeeklyLeaderboard(weeklyRes.data);
      setAlltimeLeaderboard(alltimeRes.data);
      setRivals(rivalsRes.data);
    } catch (error) {
      toast.error('Failed to load daily challenge');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Refresh data without showing loading state
  const refreshData = async () => {
    try {
      const [challengeRes, todayRes] = await Promise.all([
        dailyChallengeAPI.get(),
        dailyChallengeAPI.getLeaderboard()
      ]);
      setChallenge(challengeRes.data);
      setTodayLeaderboard(todayRes.data);
    } catch (error) {
      console.error('Polling refresh failed:', error);
    }
  };

  const handleStartChallenge = async () => {
    try {
      const response = await dailyChallengeAPI.start();
      navigate(`/daily-challenge/play/${response.data.submission_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start challenge');
    }
  };

  const formatTime = (ms) => {
    if (!ms) return '0:00';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatScore = (score) => {
    if (score >= 1000) return `${(score / 1000).toFixed(1)}k`;
    return score;
  };

  // Get current leaderboard based on period
  const getCurrentLeaderboard = () => {
    switch (leaderboardPeriod) {
      case 'weekly': return weeklyLeaderboard?.leaderboard || [];
      case 'alltime': return alltimeLeaderboard?.leaderboard || [];
      default: return todayLeaderboard?.leaderboard || [];
    }
  };

  const currentLeaderboard = getCurrentLeaderboard();
  const top3 = currentLeaderboard.slice(0, 3);
  const restOfLeaderboard = currentLeaderboard.slice(3);

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-gray-400">Loading daily challenge...</div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-gray-300">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12" />
            </div>
          </div>
        </div>
      </nav>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-[hsl(var(--brand-gold-500))]" />
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-white">Daily Challenge</h1>
          </div>
          <p className="text-gray-400 text-sm sm:text-base">{today}</p>
        </div>

        {/* Challenge Card — Top of Page */}
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] mb-6" data-testid="daily-challenge-card">
          <CardContent className="p-4 sm:p-8">
            {challenge?.user_completed ? (
              /* Already Completed View */
              <div className="text-center">
                <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-emerald-400" />
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Challenge Complete!</h2>
                <p className="text-gray-400 mb-4 sm:mb-6">You've already completed today's challenge</p>
                
                {challenge?.user_submission?.score && (
                  <div className="bg-[hsl(var(--accent))] rounded-lg p-4 sm:p-6 max-w-md mx-auto mb-4 sm:mb-6">
                    <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center">
                      <div>
                        <p className="text-2xl sm:text-3xl font-bold text-[hsl(var(--brand-gold-500))]">
                          {challenge.user_submission.score.total_score}
                        </p>
                        <p className="text-xs text-gray-400">Total Score</p>
                      </div>
                      <div>
                        <p className="text-2xl sm:text-3xl font-bold text-emerald-400">
                          {challenge.user_submission.score.correct_count}/10
                        </p>
                        <p className="text-xs text-gray-400">Correct</p>
                      </div>
                      <div>
                        <p className="text-2xl sm:text-3xl font-bold text-white">
                          {formatTime(challenge.user_submission.total_time_ms)}
                        </p>
                        <p className="text-xs text-gray-400">Time</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : challenge?.user_submission && challenge?.user_submission?.status === 'in_progress' ? (
              /* In Progress - Resume View */
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center">
                  <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
                
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Challenge In Progress</h2>
                <p className="text-gray-400 mb-4 sm:mb-6 max-w-md mx-auto">
                  You have an unfinished challenge. Resume to continue where you left off!
                </p>

                <div className="flex flex-col items-center gap-3 sm:gap-4">
                  <Button
                    onClick={() => navigate(`/daily-challenge/play/${challenge.user_submission.id}`)}
                    size="lg"
                    className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-8 sm:px-12 py-5 sm:py-6 text-lg sm:text-xl font-bold"
                    data-testid="resume-daily-challenge"
                  >
                    <Play className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                    Resume Challenge
                  </Button>
                  
                  <p className="text-xs text-gray-500">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Complete before midnight UTC to keep your streak!
                  </p>
                </div>
              </div>
            ) : (
              /* Start Challenge View */
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-gradient-to-br from-[hsl(var(--brand-gold-500))] to-[hsl(var(--brand-gold-400))] flex items-center justify-center">
                  <Zap className="w-8 h-8 sm:w-10 sm:h-10 text-[hsl(var(--brand-blue-900))]" />
                </div>
                
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Ready to Play?</h2>
                <p className="text-gray-400 mb-4 sm:mb-6 max-w-md mx-auto">
                  Answer 10 clues across 5 categories. Compete for the top spot on today's leaderboard!
                </p>

                {/* Categories Preview */}
                <div className="flex flex-wrap justify-center gap-2 mb-6 sm:mb-8">
                  {challenge?.categories?.map((cat) => (
                    <Badge 
                      key={cat}
                      className="bg-[hsl(var(--accent))] text-gray-300 border-[hsl(var(--border))]"
                    >
                      {cat}
                    </Badge>
                  ))}
                </div>

                <div className="flex flex-col items-center gap-3 sm:gap-4">
                  <Button
                    onClick={handleStartChallenge}
                    size="lg"
                    className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-8 sm:px-12 py-5 sm:py-6 text-lg sm:text-xl font-bold"
                    data-testid="start-daily-challenge"
                  >
                    <Play className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                    Start Challenge
                  </Button>
                  
                  <p className="text-xs text-gray-500">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Complete before midnight UTC to keep your streak!
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-3 sm:p-4 text-center">
              <Flame className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1.5 sm:mb-2 text-orange-400" />
              <p className="text-xl sm:text-2xl font-bold text-white">{challenge?.streak?.current || 0}</p>
              <p className="text-xs text-gray-400">Day Streak</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-3 sm:p-4 text-center">
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1.5 sm:mb-2 text-[hsl(var(--brand-gold-500))]" />
              <p className="text-xl sm:text-2xl font-bold text-white">{challenge?.streak?.best || 0}</p>
              <p className="text-xs text-gray-400">Best Streak</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-3 sm:p-4 text-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1.5 sm:mb-2 text-blue-400" />
              <p className="text-xl sm:text-2xl font-bold text-white">{challenge?.participant_count || 0}</p>
              <p className="text-xs text-gray-400">Players Today</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-3 sm:p-4 text-center">
              <Snowflake className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1.5 sm:mb-2 text-cyan-400" />
              <p className="text-xl sm:text-2xl font-bold text-white">{challenge?.streak?.freeze_count || 0}</p>
              <p className="text-xs text-gray-400">Streak Freezes</p>
            </CardContent>
          </Card>
        </div>

        {/* Leaderboard Section */}
        <div>
          {/* Period Selector */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-lg p-1">
              {[
                { id: 'today', label: 'Today', icon: Calendar },
                { id: 'weekly', label: 'This Week', icon: TrendingUp },
                { id: 'alltime', label: 'All Time', icon: Crown }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setLeaderboardPeriod(id)}
                  className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${
                    leaderboardPeriod === id
                      ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Two Column Layout: Top 3 | Rankings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* LEFT TILE: Top 3 Podium */}
                <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] overflow-hidden" data-testid="top-3-tile">
                  <CardHeader className="pb-2 px-4 sm:px-6">
                    <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                      <Crown className="w-5 h-5 text-yellow-400" />
                      Top 3 Champions
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      {leaderboardPeriod === 'today' ? "Today's" : leaderboardPeriod === 'weekly' ? 'This Week\'s' : 'All-Time'} best performers
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {top3.length >= 3 ? (
                      <div className="flex justify-center items-end gap-2 sm:gap-3 py-4">
                        {/* 2nd Place */}
                        <div className="flex flex-col items-center max-w-[100px]">
                          <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br ${MEDAL_STYLES[2].bg} p-0.5 mb-2`}>
                            <div className="w-full h-full rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center overflow-hidden">
                              {top3[1]?.avatar_url ? (
                                <img src={top3[1].avatar_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-base sm:text-lg font-bold text-gray-300">{top3[1]?.display_name?.charAt(0)}</span>
                              )}
                            </div>
                          </div>
                          <Medal className={`w-4 h-4 sm:w-5 sm:h-5 ${MEDAL_STYLES[2].text} mb-1`} />
                          <p className="text-white font-medium text-xs truncate max-w-[70px] sm:max-w-[80px]">{top3[1]?.display_name}</p>
                          {top3[1]?.featured_badges?.length > 0 && (
                            <div className="flex items-center gap-0.5 my-1">
                              {top3[1].featured_badges.slice(0, 2).map((badge, i) => (
                                <span key={i} className="text-xs">{badge.icon}</span>
                              ))}
                            </div>
                          )}
                          <p className="text-[hsl(var(--brand-gold-500))] font-bold text-xs sm:text-sm">
                            {formatScore(leaderboardPeriod === 'today' ? top3[1]?.score?.total_score : top3[1]?.total_score)}
                          </p>
                          <div className={`w-14 sm:w-16 h-20 bg-gradient-to-t from-gray-400/20 to-gray-300/10 rounded-t-lg mt-2 border-t-2 ${MEDAL_STYLES[2].border}`} />
                        </div>

                        {/* 1st Place */}
                        <div className="flex flex-col items-center -mt-6 max-w-[110px]">
                          <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 mb-1 animate-pulse" />
                          <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br ${MEDAL_STYLES[1].bg} p-0.5 mb-2`}>
                            <div className="w-full h-full rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center overflow-hidden">
                              {top3[0]?.avatar_url ? (
                                <img src={top3[0].avatar_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-lg sm:text-xl font-bold text-yellow-400">{top3[0]?.display_name?.charAt(0)}</span>
                              )}
                            </div>
                          </div>
                          <p className="text-white font-bold text-xs sm:text-sm truncate max-w-[80px] sm:max-w-[100px]">{top3[0]?.display_name}</p>
                          {top3[0]?.featured_badges?.length > 0 && (
                            <div className="flex items-center gap-0.5 my-1">
                              {top3[0].featured_badges.slice(0, 3).map((badge, i) => (
                                <span key={`badge-1st-${i}`} className="text-sm">{badge.icon}</span>
                              ))}
                            </div>
                          )}
                          <p className="text-[hsl(var(--brand-gold-500))] font-bold text-base sm:text-lg">
                            {formatScore(leaderboardPeriod === 'today' ? top3[0]?.score?.total_score : top3[0]?.total_score)}
                          </p>
                          <div className={`w-16 sm:w-20 h-28 bg-gradient-to-t from-yellow-400/20 to-yellow-300/10 rounded-t-lg mt-2 border-t-2 ${MEDAL_STYLES[1].border}`} />
                        </div>

                        {/* 3rd Place */}
                        <div className="flex flex-col items-center max-w-[100px]">
                          <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br ${MEDAL_STYLES[3].bg} p-0.5 mb-2`}>
                            <div className="w-full h-full rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center overflow-hidden">
                              {top3[2]?.avatar_url ? (
                                <img src={top3[2].avatar_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-lg font-bold text-orange-400">{top3[2]?.display_name?.charAt(0)}</span>
                              )}
                            </div>
                          </div>
                          <Award className={`w-5 h-5 ${MEDAL_STYLES[3].text} mb-1`} />
                          <p className="text-white font-medium text-xs truncate max-w-[80px]">{top3[2]?.display_name}</p>
                          {top3[2]?.featured_badges?.length > 0 && (
                            <div className="flex items-center gap-0.5 my-1">
                              {top3[2].featured_badges.slice(0, 2).map((badge, i) => (
                                <span key={`badge-3rd-${i}`} className="text-xs">{badge.icon}</span>
                              ))}
                            </div>
                          )}
                          <p className="text-[hsl(var(--brand-gold-500))] font-bold text-xs sm:text-sm">
                            {formatScore(leaderboardPeriod === 'today' ? top3[2]?.score?.total_score : top3[2]?.total_score)}
                          </p>
                          <div className={`w-14 sm:w-16 h-14 bg-gradient-to-t from-orange-400/20 to-orange-300/10 rounded-t-lg mt-2 border-t-2 ${MEDAL_STYLES[3].border}`} />
                        </div>
                      </div>
                    ) : top3.length > 0 ? (
                      <div className="space-y-3 py-4">
                        {top3.map((player, idx) => (
                          <div key={player?.display_name || `player-${idx}`} className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--accent))]/50">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br ${MEDAL_STYLES[idx + 1]?.bg || 'from-gray-400 to-gray-600'}`}>
                              <span className="text-white font-bold text-sm">{idx + 1}</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-white font-medium text-sm">{player?.display_name}</p>
                            </div>
                            <p className="text-[hsl(var(--brand-gold-500))] font-bold">
                              {formatScore(leaderboardPeriod === 'today' ? player?.score?.total_score : player?.total_score)}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Crown className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                        <p className="text-gray-400">No champions yet!</p>
                        <p className="text-gray-500 text-sm">Be the first to claim the throne.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* RIGHT TILE: Full Leaderboard Rankings */}
                <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] overflow-hidden" data-testid="leaderboard-rankings-tile">
                  <CardHeader className="pb-2 px-4 sm:px-6">
                    <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                      <Trophy className="w-5 h-5 text-[hsl(var(--brand-gold-500))] shrink-0" />
                      {leaderboardPeriod === 'today' ? "Today's" : leaderboardPeriod === 'weekly' ? 'This Week\'s' : 'All-Time'} Rankings
                    </CardTitle>
                    <CardDescription className="text-gray-400 text-xs sm:text-sm">
                      {rivals?.user_rank 
                        ? `Your position: #${rivals.user_rank} of ${rivals.total_players}`
                        : leaderboardPeriod === 'today' 
                          ? `${todayLeaderboard?.total_completed || 0} players completed`
                          : leaderboardPeriod === 'weekly'
                            ? `${weeklyLeaderboard?.total_participants || 0} players this week`
                            : `${alltimeLeaderboard?.total_participants || 0} all-time players`
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="px-4 sm:px-6">
                    <ScrollArea className="h-[340px] pr-1 sm:pr-2">
                      {currentLeaderboard.length > 0 ? (
                        <div className="space-y-2">
                          {currentLeaderboard.map((entry, idx) => {
                            const rank = idx + 1;
                            const isTop3 = rank <= 3;
                            const isCurrentUser = rivals?.rivals?.find(r => r.is_current_user && r.rank === rank);
                            
                            return (
                              <div 
                                key={entry?.display_name || `entry-${idx}`}
                                className={`flex items-center justify-between p-2 sm:p-3 rounded-lg transition-colors ${
                                  isCurrentUser 
                                    ? 'bg-[hsl(var(--brand-gold-500))]/10 border border-[hsl(var(--brand-gold-500))]/30' 
                                    : isTop3
                                      ? 'bg-[hsl(var(--accent))]/70'
                                      : 'bg-[hsl(var(--accent))]/50 hover:bg-[hsl(var(--accent))]'
                                }`}
                                data-testid={`leaderboard-rank-${rank}`}
                              >
                                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                  {/* Rank Badge */}
                                  {isTop3 ? (
                                    <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center shrink-0 bg-gradient-to-br ${MEDAL_STYLES[rank].bg}`}>
                                      {rank === 1 ? <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-white" /> : 
                                       rank === 2 ? <Medal className="w-3 h-3 sm:w-4 sm:h-4 text-white" /> : 
                                       <Award className="w-3 h-3 sm:w-4 sm:h-4 text-white" />}
                                    </div>
                                  ) : (
                                    <div className={`w-6 sm:w-7 text-center font-mono text-xs sm:text-sm shrink-0 ${isCurrentUser ? 'text-[hsl(var(--brand-gold-500))] font-bold' : 'text-gray-500'}`}>
                                      #{rank}
                                    </div>
                                  )}
                                  
                                  {/* Avatar */}
                                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full overflow-hidden shrink-0 ${isCurrentUser ? 'ring-2 ring-[hsl(var(--brand-gold-500))]' : ''}`}>
                                    {entry.avatar_url ? (
                                      <img src={entry.avatar_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <div className={`w-full h-full flex items-center justify-center text-xs sm:text-sm ${
                                        isTop3 
                                          ? `bg-gradient-to-br ${MEDAL_STYLES[rank].bg} text-white`
                                          : 'bg-[hsl(var(--brand-blue-800))] text-[hsl(var(--brand-gold-500))]'
                                      }`}>
                                        {entry.display_name?.charAt(0) || '?'}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Name & Badges */}
                                  <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                                    <span className={`text-xs sm:text-sm truncate ${isCurrentUser ? 'text-[hsl(var(--brand-gold-400))] font-medium' : 'text-white'}`}>
                                      {entry.display_name}
                                      {isCurrentUser && <span className="ml-1 text-xs text-[hsl(var(--brand-gold-500))]">(You)</span>}
                                    </span>
                                    {entry.featured_badges?.length > 0 && (
                                      <div className="flex items-center gap-0.5 shrink-0">
                                        {entry.featured_badges.slice(0, 2).map((badge, badgeIdx) => (
                                          <TooltipProvider key={badgeIdx}>
                                            <Tooltip>
                                              <TooltipTrigger>
                                                <span className={`text-xs ${
                                                  badge.tier === 'gold' ? 'text-yellow-400' :
                                                  badge.tier === 'silver' ? 'text-gray-300' :
                                                  badge.tier === 'bronze' ? 'text-orange-400' : 'text-gray-400'
                                                }`}>
                                                  {badge.icon}
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>{badge.name}</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Score */}
                                <div className="text-right shrink-0">
                                  <p className={`font-bold text-xs sm:text-sm ${isCurrentUser ? 'text-[hsl(var(--brand-gold-400))]' : 'text-[hsl(var(--brand-gold-500))]'}`}>
                                    {formatScore(leaderboardPeriod === 'today' ? entry.score?.total_score : entry.total_score)}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {leaderboardPeriod === 'today' 
                                      ? `${entry.score?.correct_count || 0}/10`
                                      : `${entry.challenges_completed || 0} games`
                                    }
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Trophy className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                          <p className="text-gray-400">No one has completed {leaderboardPeriod === 'today' ? "today's" : 'any'} challenge yet.</p>
                          <p className="text-gray-500 text-sm">Be the first to claim the top spot!</p>
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </div>
      </main>
    </div>
  );
}
